import type {
  ApiKey, AuditEvent, AuditListParams, AuthSuccess, CreateApiKeyRequest, CreateEnrollmentTokenRequest, CreateInvitationRequest,
  CreatedApiKey, CreatedEnrollmentToken, CurrentUser, DashboardSummary, Device, DeviceGroup, DeviceListParams, EnrollmentToken,
  ExportFormat, Integration, Invitation, Invoice, LoginRequest, LoginResult, Member, MemberListParams, Organization,
  OrganizationSettings, OrgId, Page, Policy, PolicyInput, PolicyPreview, PresenceEvent, Release, RemoteSession, Role,
  SessionListParams, Subscription, VerifyTotpRequest, BillingMode, PlanId, Permission, IntegrationKind, RegisterRequest,
} from './types';
import type { AddPaymentMethodRequest, PaymentMethod, PlanDefinition, PurchaseCreditsRequest } from './types';

import type { AlertSettings, SsoConfig } from './types';
import type { PolicyDelivery, PolicyPreviewInput, PolicyVersion, RecordingAccess } from './types';

import type { RatingListParams, RatingPage, RatingSummary, TechnicianRatingHistory } from './types';

import type { SettingsApi } from './settings.types';

/** Erreur API normalisée (code HTTP + code applicatif). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

export type Unsubscribe = () => void;

export interface ExportResult {
  filename: string;
  mimeType: string;
  content: string;
}

/**
 * Contrat unique entre la console et le backend.
 * Les écrans n'utilisent jamais cette interface directement : ils passent par les hooks de `hooks.ts`.
 * Deux implémentations : `HttpApiClient` (VITE_API_URL défini) et `MockApiClient` (par défaut).
 */
export interface ApiClient extends SettingsApi {
  /* Authentification */
  /** Crée un compte et une organisation dont le créateur devient propriétaire. */
  register(req: RegisterRequest): Promise<LoginResult>;
  login(req: LoginRequest): Promise<LoginResult>;
  verifyTotp(req: VerifyTotpRequest): Promise<AuthSuccess>;
  logout(): Promise<void>;
  me(): Promise<CurrentUser>;

  /* Organisation */
  listOrganizations(): Promise<Organization[]>;
  getOrganizationSettings(org: OrgId): Promise<OrganizationSettings>;
  updateOrganizationSettings(org: OrgId, patch: Partial<OrganizationSettings>): Promise<OrganizationSettings>;

  /* Tableau de bord */
  getDashboard(org: OrgId): Promise<DashboardSummary>;

  /* Parc */
  listDevices(org: OrgId, params?: DeviceListParams): Promise<Page<Device>>;
  getDevice(org: OrgId, deviceId: string): Promise<Device>;
  updateDevice(org: OrgId, deviceId: string, patch: Partial<Pick<Device, 'name' | 'groupId' | 'tags' | 'channel'>>): Promise<Device>;
  revokeDevice(org: OrgId, deviceId: string): Promise<void>;
  /** Demande la mise à jour de l'agent vers la dernière version de son canal. */
  requestDeviceUpdate(org: OrgId, deviceId: string): Promise<Device>;
  getAlertSettings(org: OrgId): Promise<AlertSettings>;
  updateAlertSettings(org: OrgId, patch: Partial<AlertSettings>): Promise<AlertSettings>;
  /** Configuration SSO (SAML / OIDC) de l'organisation ; null si jamais configuré. */
  getSsoConfig(org: OrgId): Promise<SsoConfig | null>;
  saveSsoConfig(org: OrgId, config: Omit<SsoConfig, 'lastTestedAt'> & { lastTestedAt?: string | null }): Promise<SsoConfig>;
  listDeviceGroups(org: OrgId): Promise<DeviceGroup[]>;
  createDeviceGroup(org: OrgId, input: Pick<DeviceGroup, 'name' | 'description' | 'parentId'>): Promise<DeviceGroup>;
  updateDeviceGroup(org: OrgId, groupId: string, patch: Partial<Pick<DeviceGroup, 'name' | 'description' | 'parentId'>>): Promise<DeviceGroup>;
  deleteDeviceGroup(org: OrgId, groupId: string): Promise<void>;
  /** Temps réel : présence des appareils (WebSocket côté HTTP, simulation côté mock). */
  subscribePresence(org: OrgId, handler: (events: PresenceEvent[]) => void): Unsubscribe;

  /* Personnes */
  listMembers(org: OrgId, params?: MemberListParams): Promise<Page<Member>>;
  updateMember(org: OrgId, memberId: string, patch: Partial<Pick<Member, 'roleId' | 'groupIds' | 'status'>>): Promise<Member>;
  removeMember(org: OrgId, memberId: string): Promise<void>;
  listRoles(org: OrgId): Promise<Role[]>;
  saveRole(org: OrgId, role: Pick<Role, 'name' | 'description' | 'permissions'> & { id?: string }): Promise<Role>;
  deleteRole(org: OrgId, roleId: string): Promise<void>;
  listInvitations(org: OrgId): Promise<Invitation[]>;
  createInvitation(org: OrgId, req: CreateInvitationRequest): Promise<Invitation>;
  revokeInvitation(org: OrgId, invitationId: string): Promise<void>;
  resendInvitation(org: OrgId, invitationId: string): Promise<Invitation>;
  /** Acceptation d'une invitation par l'invité (lien de l'e-mail) : le compte connecté rejoint l'organisation. */
  acceptInvitation(org: OrgId, invitationId: string): Promise<void>;

  /* Politiques */
  listPolicies(org: OrgId): Promise<Policy[]>;
  getPolicy(org: OrgId, policyId: string): Promise<Policy>;
  createPolicy(org: OrgId, input: PolicyInput): Promise<Policy>;
  updatePolicy(org: OrgId, policyId: string, input: Partial<PolicyInput>): Promise<Policy>;
  deletePolicy(org: OrgId, policyId: string): Promise<void>;
  previewPolicy(org: OrgId, input: PolicyPreviewInput): Promise<PolicyPreview>;
  /** Historique des versions (la plus récente en premier) et retour arrière (crée une nouvelle version). */
  listPolicyVersions(org: OrgId, policyId: string): Promise<PolicyVersion[]>;
  rollbackPolicy(org: OrgId, policyId: string, version: number): Promise<Policy>;
  /** Accusé de réception par agent de la version courante. */
  getPolicyDelivery(org: OrgId, policyId: string): Promise<PolicyDelivery>;

  /* Audit et sessions */
  listAuditEvents(org: OrgId, params?: AuditListParams): Promise<Page<AuditEvent>>;
  exportAudit(org: OrgId, format: ExportFormat, params?: AuditListParams): Promise<ExportResult>;
  listSessions(org: OrgId, params?: SessionListParams): Promise<Page<RemoteSession>>;
  getSession(org: OrgId, sessionId: string): Promise<RemoteSession>;
  /** Demande de lecture d'un enregistrement (soumise au consentement, journalisée). */
  accessRecording(org: OrgId, sessionId: string, reason: string): Promise<RecordingAccess>;

  /* Satisfaction */
  /** Synthèse des notes des clients (équipe et par technicien, 12 derniers mois). */
  getRatingSummary(org: OrgId): Promise<RatingSummary>;
  /** Évolution mensuelle d'un technicien (`months` : 1 à 24, 12 par défaut). */
  getTechnicianRatings(org: OrgId, technicianId: string, months?: number): Promise<TechnicianRatingHistory>;
  /** Notes et commentaires, les plus récents d'abord (pagination par curseur). */
  listRatings(org: OrgId, params?: RatingListParams): Promise<RatingPage>;

  /* Déploiement */
  listEnrollmentTokens(org: OrgId): Promise<EnrollmentToken[]>;
  createEnrollmentToken(org: OrgId, req: CreateEnrollmentTokenRequest): Promise<CreatedEnrollmentToken>;
  revokeEnrollmentToken(org: OrgId, tokenId: string): Promise<void>;
  listReleases(org: OrgId): Promise<Release[]>;
  /** Dernière version stable de l'agent publiée (référence « à jour ») ; `null` si aucune. */
  getLatestAgent(org: OrgId): Promise<string | null>;

  /* Facturation */
  /** Catalogue des plans actifs (quotas et fonctionnalités définis côté serveur). */
  listPlans(): Promise<PlanDefinition[]>;
  getSubscription(org: OrgId): Promise<Subscription>;
  /** Démarre l'essai PRO de 15 jours (une seule fois par organisation ; 409 sinon). */
  startTrial(org: OrgId): Promise<Subscription>;
  updateSubscription(org: OrgId, patch: { planId?: PlanId; billingMode?: BillingMode; technicians?: number }): Promise<Subscription>;
  listInvoices(org: OrgId): Promise<Invoice[]>;
  listPaymentMethods(org: OrgId): Promise<PaymentMethod[]>;
  addPaymentMethod(org: OrgId, req: AddPaymentMethodRequest): Promise<PaymentMethod>;
  removePaymentMethod(org: OrgId, id: string): Promise<void>;
  setDefaultPaymentMethod(org: OrgId, id: string): Promise<PaymentMethod>;
  purchaseAiCredits(org: OrgId, req: PurchaseCreditsRequest): Promise<Subscription>;
  /** Relance du paiement d'une facture impayée ; réactive l'abonnement en cas de succès. */
  retryPayment(org: OrgId): Promise<Subscription>;

  /* Paramètres */
  listIntegrations(org: OrgId): Promise<Integration[]>;
  setIntegrationConnected(org: OrgId, kind: IntegrationKind, connected: boolean): Promise<Integration>;
  listApiKeys(org: OrgId): Promise<ApiKey[]>;
  createApiKey(org: OrgId, req: CreateApiKeyRequest): Promise<CreatedApiKey>;
  revokeApiKey(org: OrgId, keyId: string): Promise<void>;
}

export type { Permission };
